import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, FileSearch, LayoutPanelLeft, FileSignature, RefreshCw, CheckCircle2, XCircle, ChevronDown } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import usePageSEO from "@/hooks/usePageSEO";

const STEPS = [
  {
    icon: MessageCircle, title: "Discovery Call (Free, 30 min)",
    body: "We listen. We ask about your stage, your goals, and what's already in place. We don't pitch products on the first call.",
    you_need: "Your time. A general sense of your situation.",
    we_do: "Listen, take notes, identify what we'd want to dig into next.",
    timeframe: "30 minutes",
  },
  {
    icon: FileSearch, title: "Comprehensive Review",
    body: "Together we gather the full picture — income, assets, debts, family structure, taxes, existing coverage. We share a secure document portal; nothing leaves your environment without consent.",
    you_need: "Recent statements (insurance, retirement accounts, brokerage), tax returns, will/trust docs if relevant.",
    we_do: "Build a clean, single-page snapshot of where you stand.",
    timeframe: "1–2 weeks",
  },
  {
    icon: LayoutPanelLeft, title: "Custom Strategy Built",
    body: "We design a layered plan across Protect / Accumulate / Distribute / Transfer. We compare 2–3 carriers per product. We share a written plan you keep.",
    you_need: "An open mind and 60 minutes for the presentation.",
    we_do: "Build the plan; compare carriers; quantify trade-offs.",
    timeframe: "1–2 weeks",
  },
  {
    icon: FileSignature, title: "Implementation",
    body: "We coordinate underwriting, applications, medical exams, and policy delivery. We handle the paperwork. We brief you at each milestone.",
    you_need: "Signatures, a 30-minute medical exam if required.",
    we_do: "Application prep, carrier coordination, delivery review.",
    timeframe: "4–8 weeks depending on underwriting",
  },
  {
    icon: RefreshCw, title: "Annual Review & Adjustments",
    body: "Life changes — kids, income, business, tax law — and so does the plan. Every year we review, adjust, and document. You always know where you stand.",
    you_need: "An hour each year.",
    we_do: "Send the review pack, walk through any changes, file updated docs.",
    timeframe: "60 minutes per year",
  },
];

const EXPECT_FROM_US = [
  { title: "Independence", body: "We compare carriers head-to-head. No carrier quotas, no preferred partners with hidden agendas." },
  { title: "Education-first", body: "If we can't explain it in 90 seconds, you shouldn't buy it." },
  { title: "Multi-carrier comparison", body: "Every recommendation includes a written carrier-comparison summary." },
  { title: "Long-term partnership", body: "Annual reviews are non-negotiable. Plans don't survive without them." },
];

const WHAT_WE_WONT = [
  { title: "Pressure-sell", body: "No artificial deadlines, no fear-based pitches, no 'today only' nonsense." },
  { title: "One-size-fits-all", body: "We don't believe in templates. Your plan is built for your situation." },
  { title: "Lock you in inappropriately", body: "Permanent products require a real fit. If term is right, we'll say so." },
];

const FAQS = [
  { q: "How long does the full process take?", a: "Discovery → strategy delivery is typically 2–4 weeks. Implementation depends on underwriting (4–8 weeks for life and DI policies). Total: ~6–12 weeks from first call to issued policies." },
  { q: "What does it cost upfront?", a: "Nothing. Our discovery call is free; the planning work is included. We're compensated by insurance carriers when policies issue — exactly the same as if you went directly to the carrier." },
  { q: "How are you paid?", a: "Commissions paid by insurance carriers, fully disclosed. We have no incentive to recommend the wrong product for you — our reputation depends on the right fit." },
  { q: "Do I have to commit to buying anything?", a: "No. You can walk away after any step. We've had clients take 6 months to decide. We've had clients decide our plan wasn't right and use it as a reference elsewhere. We're fine with either." },
  { q: "What if I already have policies?", a: "Great — bring them. We review what you have, identify gaps and overlaps, and only recommend changes if there's a clear case. Often the right answer is: keep what's working, add what's missing." },
  { q: "What if I'm not ready?", a: "Then you're not ready. Book a call when you are. We don't send chase emails." },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`process-faq-${idx}`} className="border-b border-lw-navy/10 py-4">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-start justify-between w-full text-left gap-4">
        <span className="font-sans-ui font-semibold text-lw-navy">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 mt-1 text-lw-navy/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-sm text-lw-muted leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Process() {
  usePageSEO({
    title: "Our Process — Discovery to long-term partnership",
    description: "What it's like to work with Lifespan Wealth Partners — a 5-step process from discovery call through annual reviews. No pressure, education-first.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  return (
    <div data-testid="process-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Our process</span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05] max-w-3xl">
            What it&apos;s like to work with us.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            Five steps. Education-first. No pressure. Most clients tell us this looks nothing like what they expected from an insurance conversation.
          </p>
        </section>

        {/* Timeline */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
          <div data-testid="process-timeline" className="space-y-6">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="rounded-2xl bg-white border border-lw-navy/10 p-6 sm:p-8">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, rgba(11,37,69,0.08), rgba(212,162,76,0.18))", color: "#0B2545" }}>
                    <s.icon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs uppercase tracking-[0.18em] text-lw-gold font-semibold">Step {i + 1}</span>
                    <h3 className="mt-1 font-serif-display text-xl text-lw-navy">{s.title}</h3>
                    <p className="mt-3 text-sm text-lw-navy/85 leading-relaxed">{s.body}</p>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <Micro label="What you'll need" value={s.you_need} />
                      <Micro label="What we'll do" value={s.we_do} />
                      <Micro label="Typical timeframe" value={s.timeframe} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Expect from us */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div data-testid="expect-from-us">
            <span className="lw-eyebrow">What to expect from us</span>
            <div className="mt-4 space-y-3">
              {EXPECT_FROM_US.map((p, i) => (
                <div key={i} className="rounded-xl bg-white border border-lw-navy/10 p-4 flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-lw-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-sans-ui font-semibold text-sm text-lw-navy">{p.title}</p>
                    <p className="mt-1 text-sm text-lw-muted">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="wont-do">
            <span className="lw-eyebrow">What we&apos;ll never do</span>
            <div className="mt-4 space-y-3">
              {WHAT_WE_WONT.map((p, i) => (
                <div key={i} className="rounded-xl bg-lw-ivory-warm border border-lw-navy/10 p-4 flex items-start gap-3">
                  <XCircle size={18} className="text-lw-navy/40 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-sans-ui font-semibold text-sm text-lw-navy">{p.title}</p>
                    <p className="mt-1 text-sm text-lw-muted">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <span className="lw-eyebrow">Process FAQ</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">Common questions about how this works.</h2>
          <div data-testid="process-faqs" className="mt-8">
            {FAQS.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} />)}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
          <div className="rounded-3xl bg-lw-navy text-lw-ivory p-8 sm:p-12 relative overflow-hidden">
            <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full" style={{ background: "radial-gradient(circle, rgba(212,162,76,0.2) 0%, rgba(11,37,69,0) 70%)" }} />
            <div className="relative max-w-2xl">
              <span className="text-xs uppercase tracking-[0.2em] text-lw-gold font-semibold">Ready for step 1?</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl leading-[1.1]">Book your discovery call.</h2>
              <p className="mt-3 text-sm sm:text-base text-lw-ivory/70 leading-relaxed">Free. 30 minutes. No pitch.</p>
              <Link to="/apply?source=process" data-testid="process-cta" className="btn-gold mt-6 inline-flex">Book a free discovery call <ArrowRight size={18} /></Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Micro({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-lw-navy/55 font-semibold">{label}</p>
      <p className="mt-1 text-sm text-lw-navy/85 leading-snug">{value}</p>
    </div>
  );
}
